import React, { FunctionComponent, PropsWithChildren, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { ButtonBackgroundColor, ComponentSize, Display, JustifyContent, Layout, MessageWindow, ModalWindow, SpacingSize, TextButton, TextColor } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames, createQA } from "@multiweb/reactUtils";
import { IAppState } from "@multiweb/types/react";
import { createSetTouchedAction } from "../../actions";
import { LOC_CANCEL, LOC_CLOSE, LOC_DISCARD, LOC_DISCARD_CHANGES, LOC_OK, LOC_SAVE } from "../../locKeys";
import { isWorkflowReadOnly } from "../../selectors";
import { MessageWindowState, WorkflowEditorProps } from "../../types";

type EditDialogComponentProps = PropsWithChildren<{
    /** Šířka okna v pixelech */
    readonly width: number;
    /** Výška okna v pixelech */
    readonly height: number;
    /** Minimální šířka okna v pixelech */
    readonly minWidth?: number;
    /** Minimální výška okna v pixelech */
    readonly minHeight?: number;
    /** Přepínač informující o tom, zdali je možno kliknout na tlačítko uložit. */
    readonly canSave?: boolean;
    /** Přepínač určující, zdali je dialog viditelný */
    readonly isHidden?: boolean;
    /** Přepínač určující, zdali je workflow readonly. */
    readonly isWorkflowReadOnly: boolean;
    /** Příznak indukující, zdali v editačním dialogu došlo k nějaké změně. */
    readonly touched: boolean;
    /** Callback volaný při kliknutí na tlačítko Uložit */
    readonly onSaveClick: () => Promise<void>;
    /** Callback volaný při kliknutí na tlačítko Zrušit */
    readonly onCancelClick: () => void;
    /** Callback volaný při zavření okna (uzavření si řídí programátor sám, externě, zde se aplikuje navíc timeout, aby se vykonala animace) */
    readonly onCloseEdit: () => void;
    /** Funkce umožňuje zpětně nastavit, zdali byl editační formulář editovaný či ne. Například po uložení (ale ne zavření) musíme nastavit isTouched na false. */
    readonly setTouched: (touched: boolean) => void;
    /** Funkce umožňuje nastavit, zdali bylo celé workflow nějakým způsobem editované, nevztahuje se na editační formulář, ale na celé WF. */
    readonly setIsWfTouched: (touched: boolean) => void;
    readonly closeText: string;
    readonly saveText: string;
    readonly discardChangesText: string;
}>;

const classNames = bemClassNames("edit-dialog-component");
const editDialogClassName = classNames();
const containerClassName = classNames("content");

const editDialogComponentDataQA = createQA("edit-dialog-component");
const contentSectionDataQA = createQA("content-section");

const EditDialogComponent: FunctionComponent<EditDialogComponentProps> = ({
    children,
    width,
    height,
    minWidth = 540,
    minHeight = 360,
    canSave = true,
    isHidden,
    isWorkflowReadOnly,
    touched,
    onSaveClick,
    onCancelClick,
    onCloseEdit,
    setTouched,
    setIsWfTouched,
    closeText,
    saveText,
    discardChangesText,
}) => {
    const [isDisplayed, setIsDisplayed] = useState(true);
    const [isMessageBoxShown, setIsMessageBoxShown] = useState<MessageWindowState>({ isOpen: false, properties: null });

    const closeEdit = () => {
        setIsDisplayed(false);
    };

    const cancelEdits = () => {
        onCancelClick();
        closeEdit();
    };

    const onCloseMessageWindow = () => {
        setIsMessageBoxShown({ isOpen: false, properties: null });
    };

    const showSaveErrorMessage = (errorMessage: string) => setIsMessageBoxShown({
        isOpen: true,
        properties: {
            children: errorMessage,
            primaryButtonLocKey: LOC_OK,
            onCloseMessageWindow,
        },
    });

    const handleCloseClick = () => {
        if (touched)
            setIsMessageBoxShown({
                isOpen: true,
                properties: {
                    children: discardChangesText,
                    primaryButtonLocKey: LOC_DISCARD,
                    secondaryButtonLocKey: LOC_CANCEL,
                    onPrimaryButtonClick: cancelEdits,
                    onCloseMessageWindow,
                },
            });
        else
            closeEdit();
    };

    const handleSaveClick = async () => {
        try {
            await onSaveClick();
            setTouched(false); // po uložení není potřeba vyhazovat hlášku potvrzující záměr zavření Edit okna
            setIsWfTouched(true);
        } catch (e) {
            showSaveErrorMessage(e instanceof Error ? e.message : "There was an error while saving dialog");
        }
    };

    const handleSaveAndCloseClick = async () => {
        try {
            await onSaveClick();
            setIsWfTouched(true);
            closeEdit();
        } catch (e) {
            showSaveErrorMessage(e instanceof Error ? e.message : "There was an error while saving dialog");
        }
    };

    return (
        <ModalWindow draggable resizable hasTransparentBackdrop onCloseChange={onCloseEdit} {...{ isDisplayed, isHidden, defaultWidth: width, defaultHeight: height, minWidth, minHeight }}>
            <div {...editDialogComponentDataQA} className={editDialogClassName}>
                <div {...contentSectionDataQA} className={containerClassName}>
                    {children}
                </div>
                <Layout qa={"buttons-section"} display={Display.flex} justifyContent={JustifyContent.flexEnd} spacingSize={SpacingSize.m}>
                    <TextButton onClick={handleCloseClick} qa={"btnClose"} text={closeText} size={ComponentSize.large} color={TextColor.colorPrimary} />
                    <Layout spacingSizeLeft={SpacingSize.s} />
                    <TextButton onClick={handleSaveClick} qa={"btnSave"} text={saveText} disabled={(!canSave || isWorkflowReadOnly) || !touched} size={ComponentSize.large} backgroundColor={ButtonBackgroundColor.buttonPrimary} color={TextColor.whiteBlack} />
                    <Layout spacingSizeLeft={SpacingSize.s} />
                    <TextButton onClick={handleSaveAndCloseClick} qa={"btnSaveAndClose"} text={`${saveText} & ${closeText}`} disabled={(!canSave || isWorkflowReadOnly) || !touched} size={ComponentSize.large} backgroundColor={ButtonBackgroundColor.buttonPrimary} color={TextColor.whiteBlack} />
                </Layout>
            </div>
            {(isMessageBoxShown.isOpen && isMessageBoxShown.properties !== null) &&
                <MessageWindow {...isMessageBoxShown.properties} />
            }
        </ModalWindow>
    );
};

type StateProps = Pick<EditDialogComponentProps, "isWorkflowReadOnly" | "closeText" | "saveText" | "discardChangesText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    isWorkflowReadOnly: isWorkflowReadOnly(state, hostKey),
    closeText: getPrimaryText(state, LOC_CLOSE) ?? "Zavřít",
    saveText: getPrimaryText(state, LOC_SAVE) ?? "Uložit",
    discardChangesText: getPrimaryText(state, LOC_DISCARD_CHANGES) ?? "Opravdu chcete zahodit všechny provedené změny?",
});

type DispatchProps = Pick<EditDialogComponentProps, "setIsWfTouched">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    setIsWfTouched: (touched) => dispatch(createSetTouchedAction(hostKey, touched)),
});

export const EditDialog = connect(mapStateToProps, mapDispatchToProps)(EditDialogComponent);
